import { AxiosRequestConfig } from "axios";
import { callExternalApi } from "./external-api.service";
import { AppError } from "models/app-error";

export type VgApiResult<T> = {
  data: T | null;
  error: AppError | null;
};

const vgApiBaseUrl =
  process.env.REACT_APP_CSHARP_API_URL ||
  process.env.REACT_APP_API_SERVER_URL ||
  "http://localhost:3010";

/** Core API responses include `func_status`; `1` is `ENUM_Cmd_Status` OK in this stack. */
function vgBusinessErrorFromBody(data: unknown): AppError | null {
  if (data == null || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const status = d.func_status ?? d.Func_Status;
  if (status === undefined || status === null) return null;
  if (status === 1) return null;
  const msg = String(d.func_msg ?? d.Func_Msg ?? "Request rejected by server");
  return { message: msg };
}

export const vgApiRequest = async <T>(
  accessToken: string,
  endpoint: string,
  payload: unknown
): Promise<VgApiResult<T>> => {
  const config: AxiosRequestConfig = {
    url: `${vgApiBaseUrl}/api/${endpoint}`,
    method: "POST",
    headers: {
      Accept: "application/json",
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: payload ?? {},
  };

  const { data, error } = await callExternalApi({ config });
  if (error) {
    return { data: null, error };
  }
  const biz = vgBusinessErrorFromBody(data);
  if (biz) {
    return { data: null, error: biz };
  }
  return {
    data: (data as T) ?? null,
    error: null,
  };
};

/**
 * Multipart upload helper for the controller's FileUploadModel endpoints
 * (UploadContactImage, UploadAnimalImage, UploadOrgImage, ...). Each endpoint
 * binds a fixed list of `Params` strings (in order, e.g. ["orgId", "contactId"])
 * and a `Files` list of IFormFile, so we replicate that shape here.
 */
export const vgApiUpload = async <T>(
  accessToken: string,
  endpoint: string,
  files: File[],
  params: Array<string | number>
): Promise<VgApiResult<T>> => {
  const formData = new FormData();
  files.forEach((f) => formData.append("Files", f));
  params.forEach((p) => formData.append("Params", String(p)));

  const config: AxiosRequestConfig = {
    url: `${vgApiBaseUrl}/api/${endpoint}`,
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: formData,
  };

  const { data, error } = await callExternalApi({ config });
  if (error) {
    return { data: null, error };
  }
  const biz = vgBusinessErrorFromBody(data);
  if (biz) {
    return { data: null, error: biz };
  }
  return {
    data: (data as T) ?? null,
    error: null,
  };
};
